import { Box, Card, Container, SimpleGrid, Stack, Title, Loader, Text, Center } from "@mantine/core";
import TeamProfileCard from "components/TeamProfileCard";
import TeamStatsSection from "pages/Team/TeamStatsSection";
import UserMatchFeed, { MatchItem } from "components/profile/UserMatchFeed";
import UserNewFeed from "components/profile/UserNewFeed";
import { useParams } from "react-router-dom";
import {
  useFranchiseDetailsQuery,
  useFranchiseTeamsQuery,
  useGetMatchHistoryQuery,
  PlayerTeam,
} from "generated/graphql";

/**
 * FranchiseProfile page (Mantine layout)
 *
 * Responsive 3-row dashboard:
 * - Row 1: Franchise summary (left) + Teams list (right) - stacks on mobile
 * - Row 2: Aggregate Franchise stats (full width)
 * - Row 3: Match feed (left) + News feed (right) - stacks on mobile
 *
 * NOTE: On mobile, content scrolls. On desktop (sm+), uses fixed viewport height.
 */
export default function FranchiseProfile() {
  const GUTTER = 'md' as const;
  const { franchiseId } = useParams<{ franchiseId: string }>();

  // Fetch franchise details
  const { data: franchiseData, loading: franchiseLoading, error: franchiseError } = useFranchiseDetailsQuery({
    variables: { franchiseId: franchiseId! },
    skip: !franchiseId,
  });

  // Fetch teams in this franchise
  const { data: teamsData, loading: teamsLoading } = useFranchiseTeamsQuery({
    variables: { franchiseId: franchiseId! },
    skip: !franchiseId,
  });

  // Get all team IDs to fetch matches
  const teamIds = (teamsData?.teams?.items ?? []).map((t) => t.id);

  // Fetch match history for all teams in the franchise
  // TODO: This is inefficient - consider adding a franchise-level matches resolver
  // that aggregates matches across all teams in the franchise.
  const { data: matchData, loading: matchLoading } = useGetMatchHistoryQuery({
    variables: {
      where: teamIds.length > 0 ? {
        or: [
          ...teamIds.map((id) => ({ homeTeamId: { equals: id } })),
          ...teamIds.map((id) => ({ awayTeamId: { equals: id } })),
        ],
      } : undefined,
      order: [{ scheduledStartTime: 'DESC' as any }],
      take: 20,
    },
    skip: !franchiseId || teamIds.length === 0,
  });

  if (franchiseLoading) {
    return (
      <Center style={{ height: 'calc(100dvh - 48px)' }}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (franchiseError || !franchiseData?.franchise) {
    return (
      <Center style={{ height: 'calc(100dvh - 48px)' }}>
        <Text c="red">Failed to load franchise details.</Text>
      </Center>
    );
  }

  const franchise = franchiseData.franchise;

  // Map teams from API
  const teams = (teamsData?.teams?.items ?? []).map((t) => ({
    id: t.id,
    name: t.name,
    tier: t.tier ?? undefined,
    logoUrl: franchise.logoUrl ?? undefined, // Teams use franchise logo for now
    franchise: franchise.name,
  }));

  // Calculate aggregate franchise stats from match history
  // TODO: Replace with a dedicated stats service/endpoint when available.
  const matches = matchData?.listGameSeries?.items ?? [];
  // GameSeries status is a string - "Completed", "Scheduled", etc.
  const completedMatches = matches.filter((m) => m.status === 'Completed');

  let wins = 0;
  let losses = 0;
  let draws = 0;
  let roundDiff = 0;

  completedMatches.forEach((match) => {
    const isHome = teamIds.includes(match.homeTeamId);
    let teamScore = 0;
    let opponentScore = 0;

    // Sum up game scores
    (match.games ?? []).filter((g): g is NonNullable<typeof g> => g !== null).forEach((game) => {
      const homeScore = game.homeTeamScore ?? 0;
      const awayScore = game.awayTeamScore ?? 0;
      if (isHome) {
        teamScore += homeScore;
        opponentScore += awayScore;
      } else {
        teamScore += awayScore;
        opponentScore += homeScore;
      }
    });

    roundDiff += (teamScore - opponentScore);

    // Determine match outcome based on games won
    // PlayerTeam.TeamA = Home, PlayerTeam.TeamB = Away
    const gamesWon = (match.games ?? []).filter((g): g is NonNullable<typeof g> => g !== null).filter((g) => {
      if (isHome) return g.winningTeam === PlayerTeam.TeamA;
      return g.winningTeam === PlayerTeam.TeamB;
    }).length;
    const gamesLost = (match.games ?? []).filter((g): g is NonNullable<typeof g> => g !== null).filter((g) => {
      if (isHome) return g.winningTeam === PlayerTeam.TeamB;
      return g.winningTeam === PlayerTeam.TeamA;
    }).length;

    if (gamesWon > gamesLost) wins++;
    else if (gamesLost > gamesWon) losses++;
    else draws++;
  });

  const franchiseStats = {
    matchesPlayed: completedMatches.length,
    wins,
    losses,
    draws,
    roundDiff,
  };

  // Map matches to feed format
  const now = new Date();
  const franchiseMatches: MatchItem[] = matches.slice(0, 5).map((match) => {
    const isHome = teamIds.includes(match.homeTeamId);
    const ourTeam = isHome ? match.homeTeam?.name : match.awayTeam?.name;
    const opponent = isHome ? match.awayTeam?.name : match.homeTeam?.name;
    const scheduledAt = match.scheduledStartTime ?? match.startTime ?? '';
    const isPast = match.status === 'Completed' || (scheduledAt && new Date(scheduledAt) < now);

    // Calculate result for completed matches
    let result: 'W' | 'L' | 'D' | undefined;
    let score: string | undefined;

    if (match.status === 'Completed' && match.games?.length) {
      const validGames = match.games.filter((g): g is NonNullable<typeof g> => g !== null);
      const gamesWon = validGames.filter((g) => {
        if (isHome) return g.winningTeam === PlayerTeam.TeamA;
        return g.winningTeam === PlayerTeam.TeamB;
      }).length;
      const gamesLost = validGames.filter((g) => {
        if (isHome) return g.winningTeam === PlayerTeam.TeamB;
        return g.winningTeam === PlayerTeam.TeamA;
      }).length;

      if (gamesWon > gamesLost) result = 'W';
      else if (gamesLost > gamesWon) result = 'L';
      else result = 'D';

      score = `${gamesWon}-${gamesLost}`;
    }

    return {
      id: match.id,
      opponent: `${ourTeam} vs ${opponent}`,
      scheduledAt,
      result,
      score,
      tournament: match.tier?.tierName,
      type: isPast ? 'past' : 'upcoming',
    } as MatchItem;
  });

  return (
    <Container fluid p={{ base: "sm", sm: GUTTER }} className="h-auto sm:h-[calc(100dvh-48px)]">
      {/* Mobile: scrollable content; Desktop: fixed viewport height */}
      <div className="flex flex-col gap-3 sm:h-full sm:min-h-0">
        {/* Row 1: Franchise summary + Teams list - stacks on mobile */}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" className="sm:flex-1 sm:min-h-0">
          {/* Franchise summary card */}
          <Box className="min-h-[200px] sm:h-full sm:min-h-0">
            <Card withBorder radius="md" p="md" className="h-full flex">
              <TeamProfileCard
                logoUrl={franchise.logoUrl ?? undefined}
                teamName={franchise.name}
                franchise={undefined}
                tier={undefined}
                fullHeight
              />
            </Card>
          </Box>

          {/* Teams list in this franchise */}
          <Box className="min-h-[250px] sm:h-full sm:min-h-0">
            <Card withBorder radius="md" p="md" className="h-full flex">
              <Stack gap="sm" className="flex-1 min-h-0">
                <Title order={4}>Teams</Title>
                <div className="flex-1 min-h-0 overflow-y-auto">
                  {teamsLoading ? (
                    <Center><Loader size="sm" /></Center>
                  ) : teams.length === 0 ? (
                    <Text c="dimmed" size="sm">No teams in this franchise.</Text>
                  ) : (
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                      {teams.map((t) => (
                        <TeamProfileCard
                          key={t.id}
                          logoUrl={t.logoUrl}
                          teamName={t.name}
                          franchise={t.franchise}
                          tier={t.tier}
                          compact
                        />
                      ))}
                    </SimpleGrid>
                  )}
                </div>
              </Stack>
            </Card>
          </Box>
        </SimpleGrid>

        {/* Row 2: Franchise stats */}
        <Box className="sm:flex-[0.8] sm:min-h-0">
          <TeamStatsSection stats={franchiseStats} title="Franchise Stats" fullHeight />
        </Box>

        {/* Row 3: Match feed + News feed - stacks on mobile */}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" className="sm:flex-[1.2] sm:min-h-0">
          <Box className="min-h-[300px] sm:h-full sm:min-h-0">
            <UserMatchFeed
              title="Franchise Match Feed"
              items={matchLoading ? [] : franchiseMatches}
              fullHeight
              moreDetailsTo={`/franchises/${franchiseId}/matches`}
              emptyMessage={matchLoading ? "Loading matches..." : "No matches found."}
            />
          </Box>
          <Box className="min-h-[300px] sm:h-full sm:min-h-0">
            {/* TODO: News feed - no news data source exists yet.
                When a news/announcements feature is implemented, wire this up.
                For now, showing empty state. */}
            <UserNewFeed
              title="Franchise News Feed"
              items={[]}
              fullHeight
              moreDetailsTo={`/franchises/${franchiseId}/news`}
            />
          </Box>
        </SimpleGrid>
      </div>
    </Container>
  );
}
